import { useCallback, useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import { useLocales } from 'src/locales';
import {
  downloadTypedWorkbook,
  MAX_EXPORT_ROWS,
  type XlsxWorkbookSpec,
} from 'src/utils/file-downloader';

/**
 * NAT-2481 — one export of a BI stats table: ask for every row the view's filters match, shape
 * it, download it. `build` owns the request; it must use the same endpoint and request as the
 * screen, so the export can never see more than the screen could.
 */
export function useStatsExport(build: () => Promise<XlsxWorkbookSpec | null>) {
  const { t } = useLocales();
  const [exporting, setExporting] = useState(false);

  const run = useCallback(async () => {
    setExporting(true);
    try {
      const spec = await build();
      const count = spec?.sheets.reduce((n, s) => Math.max(n, s.rows.length), 0) ?? 0;
      if (!spec || count === 0) {
        enqueueSnackbar(t('export_nessun_dato'));
      } else if (count > MAX_EXPORT_ROWS) {
        enqueueSnackbar(t('export_troppe_righe', { count }), { variant: 'warning' });
      } else {
        downloadTypedWorkbook(spec);
      }
    } catch {
      enqueueSnackbar(t('export_non_riuscito'), { variant: 'error' });
    } finally {
      setExporting(false);
    }
  }, [build, t]);

  return { exporting, run };
}
